import React, {memo} from "react";
import {UserOutlined} from "@ant-design/icons";
import {Avatar} from "antd";
import {useNavigate} from "react-router";
import {useSnapshot} from "valtio/index";
import dayjs from "dayjs";
import {VIPStore} from "@/utils/store/vip";
import { supabaseAuthStore } from "@/utils/store/supabaseAuth";
import CommonLoading from "@/components/CommonLoading";

interface Props {}

const Index: React.FC<Props> = memo(() => {
    const navigate = useNavigate()
    // 只使用 Supabase 认证状态
    const supabaseAuth = useSnapshot(supabaseAuthStore);
    const { data: vipData } = useSnapshot(VIPStore);

    let vipNotice = "已过期"
    let vipTypeNotice = "体验会员"
    let expireNotice = 0
    if(vipData?.productTypeCur != null && vipData.expireTimeCur != null){
        vipNotice = dayjs.unix(vipData.expireTimeCur).format("YYYY-MM-DD")
        expireNotice = Math.ceil((vipData.expireTimeCur - dayjs().unix()) / 3600 / 24);
        vipTypeNotice = vipData.productTypeCur
    }

    return (
      <div className="bg-white min-h-full">
        {/* 页面标题 */}
        <header className="w-full max-w-5xl mx-auto px-12 pt-8 pb-4">
          <h1 className="text-2xl font-bold tracking-tight text-[#37352F]">个人中心</h1>
        </header>

        <div className="w-full max-w-5xl mx-auto px-12 flex flex-col gap-8 pb-16">
          {/* 用户信息区域 */}
          <section className="border border-[#E9E9E7] rounded-2xl overflow-hidden">
            <div className="p-8">
              {supabaseAuth.isLoading ? (
                <CommonLoading />
              ) : (
                <div className="flex flex-row gap-5 items-center">
                  <Avatar
                    size={64}
                    icon={<UserOutlined />}
                    className="bg-gradient-to-br from-indigo-500 to-violet-500 flex-shrink-0"
                  />
                  <div className="flex flex-col gap-1">
                    <p className="text-xl font-semibold text-[#37352F] tracking-tight">
                      {supabaseAuth.user?.user_metadata?.full_name || supabaseAuth.user?.email}
                    </p>
                    <p className="text-[#91918E] text-sm">UID：{supabaseAuth.id}</p>
                    <p className="text-[#91918E] text-sm">邮箱：{supabaseAuth.user?.email}</p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* 会员信息区域 */}
          <section className="border border-[#E9E9E7] rounded-2xl overflow-hidden">
            <div className="p-8">
              <div className="flex flex-col gap-6">
                {/* 标题行 */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                      </svg>
                    </div>
                    <span className="text-base font-semibold text-[#37352F]">会员信息</span>
                  </div>
                  <span className="px-3 py-1 rounded-lg text-xs font-medium text-amber-700 bg-amber-50 border border-amber-100">
                    {vipTypeNotice}
                  </span>
                </div>

                {/* 信息卡片网格 */}
                <div className="grid grid-cols-2 gap-4">
                  {/* 会员有效期 */}
                  <div className="bg-[#FBFBFA] border border-[#E9E9E7] rounded-xl p-5">
                    <div className="flex flex-col gap-2">
                      <div className="text-sm text-[#91918E]">会员有效期</div>
                      <div className="flex flex-row gap-2 items-center">
                        <div className="text-2xl font-bold text-[#37352F]">剩余 {expireNotice} 天</div>
                        {vipTypeNotice === '体验会员' && (
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-medium text-emerald-600 bg-emerald-50">
                            限时免费
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-[#91918E]">到期时间：{vipNotice}</div>
                    </div>
                  </div>
                  {/* 转写额度 */}
                  <div className="bg-[#FBFBFA] border border-[#E9E9E7] rounded-xl p-5">
                    <div className="flex flex-col gap-2">
                      <div className="text-sm text-[#91918E]">本月转写额度</div>
                      <div className="flex flex-row gap-2 items-center">
                        <div className="text-2xl font-bold text-[#37352F]">已用 ∞/∞ 小时</div>
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-medium text-emerald-600 bg-emerald-50">
                          限时免费
                        </span>
                      </div>
                      <div className="w-full bg-[#E9E9E7] rounded-full h-1.5 mt-1">
                        <div className="bg-indigo-400 h-1.5 rounded-full transition-all" style={{width: '50%'}} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* 续费按钮 */}
                <button
                  className="w-full py-2.5 bg-black hover:bg-zinc-800 text-white text-sm font-medium rounded-xl transition-colors cursor-pointer"
                  onClick={() => navigate('/charge/default')}
                >
                  立即续费
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    )
})
Index.displayName = 'Index'

export default Index
